import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Printer } from 'lucide-react';
import { toast } from 'sonner';
import type { Asset } from '@/types/models';

interface LabelPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  asset: Asset | null;
  workOrderNumber?: string;
}

export default function LabelPreviewDialog({ open, onOpenChange, asset, workOrderNumber }: LabelPreviewDialogProps) {
  if (!asset) return null;

  function handlePrint() {
    toast.success('Printing label...');
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="h-5 w-5 text-primary" />
            Label Preview
          </DialogTitle>
        </DialogHeader>

        <div className="border-2 border-dashed border-border rounded-lg p-4 bg-card space-y-3">
          {/* Barcode visual */}
          <div className="text-center space-y-1">
            <div className="flex justify-center gap-px">
              {Array.from({ length: 30 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-foreground"
                  style={{
                    width: Math.random() > 0.5 ? 2 : 1,
                    height: 40,
                  }}
                />
              ))}
            </div>
            <p className="text-sm font-mono font-bold text-foreground tracking-wider">
              {asset.internal_barcode}
            </p>
          </div>

          <Separator />

          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
            {asset.serial_number && (
              <>
                <span className="text-muted-foreground">Serial</span>
                <span className="font-mono font-medium text-foreground">{asset.serial_number}</span>
              </>
            )}
            {asset.barcode && (
              <>
                <span className="text-muted-foreground">Barcode</span>
                <span className="font-mono font-medium text-foreground">{asset.barcode}</span>
              </>
            )}
            <span className="text-muted-foreground">Category</span>
            <span className="font-medium text-foreground">{asset.category_name || '—'}</span>

            {asset.manufacturer_name && (
              <>
                <span className="text-muted-foreground">Manufacturer</span>
                <span className="font-medium text-foreground">{asset.manufacturer_name}</span>
              </>
            )}
            {asset.model && (
              <>
                <span className="text-muted-foreground">Model</span>
                <span className="font-medium text-foreground">{asset.model}</span>
              </>
            )}
            {workOrderNumber && (
              <>
                <span className="text-muted-foreground">Work Order</span>
                <span className="font-mono font-medium text-foreground">{workOrderNumber}</span>
              </>
            )}
            {asset.notes && (
              <>
                <span className="text-muted-foreground">Notes</span>
                <span className="text-foreground truncate">{asset.notes}</span>
              </>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={handlePrint} className="gap-2">
            <Printer className="h-4 w-4" /> Print Label
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
